import { useEffect, useRef } from 'react';
import { useGoogleMap } from '@react-google-maps/api';

interface AdvancedMarkerProps {
  position: { lat: number; lng: number };
  title?: string;
  onClick?: () => void;
}

/**
 * Drop-in replacement for the deprecated <Marker> component.
 * Uses google.maps.marker.AdvancedMarkerElement under the hood.
 * Requires:
 *   1. `marker` in the loaded libraries
 *   2. `mapId` prop on the parent <GoogleMap>
 */
export const AdvancedMarker: React.FC<AdvancedMarkerProps> = ({ position, title, onClick }) => {
  const map = useGoogleMap();
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);

  useEffect(() => {
    if (!map || !google.maps.marker?.AdvancedMarkerElement) return;

    // Create the marker
    const marker = new google.maps.marker.AdvancedMarkerElement({
      map,
      position,
      title,
    });

    if (onClick) {
      marker.addListener('click', onClick);
    }

    markerRef.current = marker;

    return () => {
      marker.map = null; // remove from map on unmount
    };
  }, [map]);

  // Update position when it changes
  useEffect(() => {
    if (markerRef.current) {
      markerRef.current.position = position;
    }
  }, [position.lat, position.lng]);

  // Update title when it changes
  useEffect(() => {
    if (markerRef.current && title !== undefined) {
      markerRef.current.title = title;
    }
  }, [title]);

  return null; // Rendered imperatively via the Maps API
};
